import { Link } from 'react-router-dom';

type BrandMarkProps = {
  size?: number;
  inverse?: boolean;
  className?: string;
};

/**
 * Monogramme SGSCTIC — construit en CSS (pas d'image) :
 * tuile marine, trame acier discrète, « S » en Barlow Condensed,
 * coin orange en rappel de la charte.
 */
export function BrandMark({ size = 44, inverse = false, className = '' }: BrandMarkProps) {
  return (
    <span
      aria-hidden="true"
      className={`relative inline-flex flex-none items-center justify-center overflow-hidden rounded-[6px] ${
        inverse ? 'bg-background-50' : 'bg-primary-500'
      } ${className}`}
      style={{ width: size, height: size }}
    >
      <span
        className={`pointer-events-none absolute inset-0 ${inverse ? 'hatch-lines' : 'hatch-lines-light'} opacity-60`}
      />
      <span
        className={`relative font-heading font-semibold leading-none ${
          inverse ? 'text-primary-600' : 'text-background-50'
        }`}
        style={{ fontSize: size * 0.6, letterSpacing: '-0.02em', marginTop: -size * 0.02 }}
      >
        S
      </span>
      <span
        className="absolute bottom-0 right-0 bg-accent-500"
        style={{
          width: size * 0.34,
          height: size * 0.34,
          clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
        }}
      />
      <span
        className={`pointer-events-none absolute inset-0 rounded-[6px] ring-1 ring-inset ${
          inverse ? 'ring-primary-900/15' : 'ring-background-50/20'
        }`}
      />
    </span>
  );
}

type BrandLogoProps = {
  onDark?: boolean;
  markSize?: number;
  tagline?: string | null;
  className?: string;
  stacked?: boolean;
  taglineFrom?: 'sm' | 'xl';
};

export default function BrandLogo({
  onDark = false,
  markSize = 44,
  tagline = 'Travaux industriels lourds',
  className = '',
  stacked = false,
  taglineFrom = 'sm',
}: BrandLogoProps) {
  const wordColor = onDark ? 'text-background-50' : 'text-foreground-950';
  const taglineColor = onDark ? 'text-background-300' : 'text-foreground-500';
  const ruleColor = onDark ? 'bg-background-50/25' : 'bg-foreground-950/15';

  return (
    <Link
      to="/"
      aria-label="SGSCTIC — retour à l’accueil"
      className={`group flex flex-none items-center gap-3 ${stacked ? 'flex-col items-start gap-2' : ''} ${className}`}
    >
      <BrandMark
        size={markSize}
        inverse={onDark}
        className="transition-transform duration-500 group-hover:-rotate-2"
      />
      <span className={`flex min-w-0 flex-col ${stacked ? 'gap-1.5' : 'gap-[3px]'}`}>
        <span
          className={`font-heading font-semibold uppercase leading-none ${wordColor}`}
          style={{ fontSize: Math.round(markSize * 0.5), letterSpacing: '0.06em' }}
        >
          SGSCTIC
        </span>
        {tagline ? (
          <span
            className={`${taglineFrom === 'xl' ? 'hidden xl:flex' : 'hidden sm:flex'} items-center gap-1.5`}
          >
            <span className={`block h-px w-3 ${ruleColor}`} />
            <span
              className={`whitespace-nowrap font-label uppercase ${taglineColor}`}
              style={{ fontSize: Math.max(7, Math.round(markSize * 0.16)), letterSpacing: '0.18em' }}
            >
              {tagline}
            </span>
          </span>
        ) : null}
      </span>
    </Link>
  );
}